import { AbstractViewModel, useViewModel } from '@footsteps/shared';
import type { AttachmentImageState } from './attachment.state';
import type { AttachmentGetSignedUrlFn } from './attachment.types';

export class AttachmentImageViewModel extends AbstractViewModel<AttachmentImageState> {
  private readonly attachmentId: string;
  private readonly getSignedUrl: AttachmentGetSignedUrlFn;
  private objectUrl: string | null = null;

  constructor(attachmentId: string, getSignedUrl: AttachmentGetSignedUrlFn) {
    super({ blobUrl: null, failed: false });
    this.attachmentId = attachmentId;
    this.getSignedUrl = getSignedUrl;
  }

  load = async (): Promise<void> => {
    const { blobUrl, failed } = this.state.getValue();
    if (blobUrl || failed) return;

    try {
      const signedUrl = await this.getSignedUrl(this.attachmentId);
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      this.objectUrl = URL.createObjectURL(blob);
      this.update((state) => {
        state.blobUrl = this.objectUrl;
      });
    } catch {
      this.update((state) => {
        state.failed = true;
      });
    }
  };

  markFailed = (): void => {
    this.update((state) => {
      state.failed = true;
    });
  };

  override dispose = (): void => {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    super.dispose();
  };
}

export function useAttachmentImageViewModel(attachmentId: string, getSignedUrl: AttachmentGetSignedUrlFn) {
  return useViewModel<AttachmentImageState, AttachmentImageViewModel>(
    () => new AttachmentImageViewModel(attachmentId, getSignedUrl)
  );
}
